import React, { useContext, useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Failed from '@totem/components/ui_components/specialCases/Failed';
import Loading from '@totem/components/ui_components/specialCases/Loading';
import StatusTag from '@totem/components/ui_components/statusTag/statusTag';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Response } from '@totem/types/common';
import { isNotNull, isNull } from '@totem/utilities/common';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

type Props = {
  open: boolean;
  onActionComplete: (refresh: boolean) => void;
  onClose: () => void;
  toolName: string;
  apiEndpoint: string;
};

const GenericTenantRefreshDialog = ({
  open,
  onClose,
  apiEndpoint,
  toolName,
  onActionComplete,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);

  const [data, setData] = useState<Response[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (open && !loaded) {
      fetch(apiEndpoint, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => {
          setIsLoading(false);

          CheckResponseShowError(res);
          return res.json();
        })
        .then((result: Response[]) => {
          setData(result);

          onActionComplete(true);

          setLoaded(true);
        });
    } else {
      setLoaded(false);

      setData([]);
    }
  }, [open]);

  const columns: ColumnProps<Response>[] = [
    {
      title: 'Status',
      dataIndex: 'httpStatus',
      key: 'httpStatus',
      render: (_, item: Response) => {
        switch (item.httpStatus) {
          case 200:
            return <StatusTag status={'success'} />;
          case 302:
            return <StatusTag status={'success'} />;
          case 500:
            return <StatusTag status={'error'} />;
          default:
            return '';
        }
      },
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (_, item: Response) => {
        switch (item.severity) {
          case 5:
            return <StatusTag status={'critical'} />;
          case 4:
            return <StatusTag status={'major'} />;
          case 3:
            return <StatusTag status={'minor'} />;
          case 2:
            return <StatusTag status={'warning'} />;
          case 1:
            return <StatusTag status={'info'} />;
          case 0:
            return <StatusTag status={'ok'} />;
          default:
            return '';
        }
      },
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      render: (_, item: Response) => item.subject,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      render: (_, item: Response) => item.message,
    },
    {
      title: 'Item',
      dataIndex: 'data',
      key: 'data',
      render: (_, item: Response) =>
        isNotNull(item.data) ? item.data.toString() : '',
    },
  ];

  return (
    <Modal
      open={open}
      width={'85%'}
      styles={{
        body: {
          maxHeight: 'calc(80vh - 120px)', // adjust for header + footer
          overflowY: 'auto',
        },
      }}
      title={`${toolName} Tenant Refresh`}
      onCancel={() => {
        onClose();
      }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>,
      ]}
    >
      {isLoading && (
        <Loading
          message={`Syncing with ${toolName} API.  Please be patient...`}
        />
      )}
      {!isLoading && (isNull(data) || data.length === 0) && (
        <Failed title={`Sync with ${toolName} Failed`} />
      )}
      {!isLoading && isNotNull(data) && data.length > 0 && (
        <IBTable
          showSorterTooltip
          columns={columns}
          dataSource={isNotNull(data) ? data : []}
          loading={isLoading}
          rowKey={(record) => record.id}
          pagination={{
            defaultPageSize: 100,
            showSizeChanger: true,
          }}
        />
      )}
    </Modal>
  );
};

export default GenericTenantRefreshDialog;
